import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { Heart, Users, Handshake } from "lucide-react";

export default function CommunityOrgAuth() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  
  const [formData, setFormData] = useState({
    email: "",
    password: "",
  });

  const handleSignIn = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      const { error } = await supabase.auth.signInWithPassword({
        email: formData.email,
        password: formData.password,
      });

      if (error) throw error;
      
      toast.success("Welcome back, partner!");
      navigate("/");
    } catch (error: any) {
      toast.error(error.message || "Sign in failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[hsl(var(--community-beige))] flex flex-col relative overflow-hidden">
      {/* Background Illustration - Flat-style people collaborating */}
      <div className="absolute inset-0 opacity-10 pointer-events-none">
        <div className="absolute top-20 left-10">
          <Users className="w-32 h-32 text-[hsl(var(--community-green))]" />
        </div>
        <div className="absolute bottom-20 right-10">
          <Handshake className="w-40 h-40 text-[hsl(var(--community-coral))]" />
        </div>
        <div className="absolute top-1/2 right-1/4 -translate-y-1/2">
          <Heart className="w-24 h-24 text-[hsl(var(--community-green))]" />
        </div>
      </div>

      {/* Header */}
      <header className="p-6 relative z-10">
        <div className="flex items-center gap-2">
          <Heart className="w-8 h-8 text-[hsl(var(--community-green))]" />
          <h1 className="text-2xl font-bold text-[hsl(var(--community-green))]">RePath</h1>
        </div>
      </header>

      {/* Main Content */}
      <div className="flex-1 flex items-center justify-center p-4 relative z-10">
        <div className="w-full max-w-md">
          <div className="text-center mb-8">
            <h2 className="text-3xl font-bold text-[hsl(var(--community-green))] mb-2">
              Welcome, Partner
            </h2>
            <p className="text-foreground/80">
              Let's build pathways to success, together.
            </p>
          </div>

          {/* Login Form */}
          <div className="bg-white rounded-lg shadow-lg p-8 border border-[hsl(var(--community-green))]/20">
            <form onSubmit={handleSignIn} className="space-y-5">
              <div className="space-y-2">
                <Label htmlFor="email">Organisation Email</Label>
                <Input
                  id="email"
                  type="email"
                  value={formData.email}
                  onChange={(e) =>
                    setFormData({ ...formData, email: e.target.value })
                  }
                  placeholder="your@organisation.org"
                  className="border-border"
                  required
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="password">Password</Label>
                <Input
                  id="password"
                  type="password"
                  value={formData.password}
                  onChange={(e) =>
                    setFormData({ ...formData, password: e.target.value })
                  }
                  placeholder="Enter your password"
                  className="border-border"
                  required
                />
              </div>

              <div className="text-right">
                <a href="#" className="text-sm text-[hsl(var(--community-coral))] hover:underline">
                  Forgot Password?
                </a>
              </div>

              <Button
                type="submit"
                disabled={loading}
                className="w-full bg-[hsl(var(--community-green))] text-white hover:bg-[hsl(var(--community-green))]/90 font-semibold py-6 text-lg"
              >
                {loading ? "Signing in..." : "Log In"}
              </Button>
            </form>

            <div className="mt-6 text-center">
              <p className="text-sm text-muted-foreground">
                Not a partner yet?{" "}
                <a href="/community-org-signup" className="text-[hsl(var(--community-green))] hover:underline font-semibold">
                  Join Our Network
                </a>
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Footer */}
      <footer className="p-6 border-t border-border/50 relative z-10">
        <div className="flex flex-wrap justify-center gap-6 text-sm text-muted-foreground">
          <a href="/privacy" className="hover:text-foreground transition-colors">
            Privacy Policy
          </a>
          <a href="/terms" className="hover:text-foreground transition-colors">
            Terms of Service
          </a>
          <a href="mailto:admin@sccapp.co.uk" className="hover:text-foreground transition-colors">
            Contact Support
          </a>
        </div>
      </footer>
    </div>
  );
}
